import Link from "next/link"
import { motion } from "framer-motion"
import { useCallback, useMemo } from "react"
import Head from "next/head"
import { Button } from "../components/Button"
import { Check } from "../components/Check"
import { Cross } from "../components/Cross"
import { Footer } from "../components/Footer"
import { Header } from "../components/Header"
import { Row } from "../components/Row"
import { Skeleton } from "../components/Skeleton"
import { useAuth } from "../utils/useAuth"
import { PageProps } from "../utils/types"

function c(amount: number, currency: string | undefined): string {
  if (!currency) {
    return ""
  }
  const f = new Intl.NumberFormat(undefined, {
    currency,
    notation: amount % 1 != 0 ? "standard" : "compact",
    style: "currency",
  }).format(amount)
  // Remove the space between the currency symbol and the amount.
  if (f.charCodeAt(1) === 160) {
    return f[0] + f.slice(2)
  } else {
    return f
  }
}

const headerVariants = {
  animate: {
    opacity: 1,
  },
  initial: {
    opacity: 0,
  },
}

const rowVariants1 = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.05,
    },
  },
  initial: {
    opacity: 0,
  },
}

const rowVariants2 = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.1,
    },
  },
  initial: {
    opacity: 0,
  },
}

export default function PricingPage(props: PageProps) {
  const { user } = useAuth()
  const curr = props.prices?.[0]?.currency as string
  const monthlyPrice: number | undefined = useMemo(() => {
    return props.prices?.find((p) => p.product_title === "Professional Monthly")
      ?.price.net as number
  }, [props.prices])
  const annualPrice: number | undefined = useMemo(() => {
    return props.prices?.find(
      (p) => p.product_title === "Professional Annually"
    )?.price.net as number
  }, [props.prices])
  const alreadySubscribed = !!(user && props.frontenderSettings?.plan)
  const monthlyProductId =
    process.env.NEXT_PUBLIC_PADDLE_PLAN_ID_PROFESSIONAL_MONTHLY
  const yearlyProductId =
    process.env.NEXT_PUBLIC_PADDLE_PLAN_ID_PROFESSIONAL_ANNUALLY
  const subscribe = useCallback(() => {
    ;(window as any).Paddle.Checkout.open({
      allowQuantity: false,
      message: "Unlimited conversions and access to all features.",
      product: monthlyProductId,
      success: "https://www.frontender.io/account?buy=success",
      title: `Frontender Professional Monthly`,
    })
  }, [monthlyProductId])
  const buy = useCallback(() => {
    ;(window as any).Paddle.Checkout.open({
      allowQuantity: false,
      message:
        "Unlimited conversions and access to all features. Best value: get 3 months a year for free.",
      product: yearlyProductId,
      success: "https://www.frontender.io/account?buy=success",
      title: `Frontender Professional Annually`,
    })
  }, [yearlyProductId])
  const [currentYear, currentSeason, seasonIcon] = useMemo(() => {
    const date = new Date()
    const year = date.getFullYear()
    const month = date.getMonth()
    if (month >= 0 && month < 3) {
      return [year, "winter", "❄️"]
    } else if (month >= 3 && month < 6) {
      return [year, "spring", "🌸"]
    } else if (month >= 6 && month < 9) {
      return [year, "summer", "🌞"]
    } else {
      return [year, "autumn", "🍂"]
    }
  }, [])
  return (
    <>
      <Head>
        <title>Frontender | Speed up your front-end workflow</title>
        <meta name="theme-color" content="#18181b" />
        <meta property="og:image" content="https://www.frontender.io/og.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="627" />
        <meta property="og:title" content="Frontender" />
        <meta
          property="description"
          content="Speed up your front-end workflow."
        />
        <meta
          property="og:description"
          content="Speed up your front-end workflow."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.frontender.io/" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="msapplication-TileColor" content="#da532c" />
      </Head>
      <motion.div
        initial="initial"
        animate="animate"
        className="flex min-h-screen select-none bg-gradient-to-b from-zinc-900 via-zinc-900 to-zinc-800 p-4"
      >
        <div className="flex flex-1 flex-col overflow-hidden border-zinc-700 p-4 pt-1 sm:border sm:p-7 sm:pt-3">
          <Header />
          <motion.div
            variants={headerVariants}
            className="flex w-full max-w-[1120px] flex-1 flex-col p-0 text-zinc-200 sm:max-w-[1280px] sm:p-2 lg:mt-8 lg:px-8 lg:pb-16"
          >
            <motion.div
              variants={headerVariants}
              className="inline-block bg-gradient-to-r from-sky-300 to-fuchsia-300 bg-clip-text pb-1 pt-10 text-3xl font-semibold leading-tight text-transparent sm:text-5xl sm:tracking-tight lg:px-4"
            >
              Get Frontender for Figma
            </motion.div>
            <motion.p
              variants={rowVariants1}
              className="mb-6 mt-3 text-xl leading-relaxed text-zinc-300 lg:px-4"
            >
              Develop faster with unlimited conversions, instant code, and more.
            </motion.p>
            <div className="mb-12 self-start rounded-lg bg-yellow-500/50 px-4 py-2 text-sm">
              {seasonIcon}{" "}
              <span className="ml-1 font-bold">
                {currentYear} {currentSeason} sale!
              </span>{" "}
              Subscribe now and get a{" "}
              <span className="font-bold">37.5% discount</span> on all
              Frontender plans.
            </div>
            {props.error && (
              <div className="mb-12 self-start rounded-lg bg-purple-900/50 px-4 py-2 text-sm">
                We couldn&apos;t fetch up-to-date prices in your local currency.
                Default prices in euros are shown instead.
              </div>
            )}
            <div className="relative grid-cols-[1fr_200px_200px_200px] pb-6 pt-6 lg:grid lg:space-y-0 xl:grid-cols-[1fr_260px_260px_260px]">
              <div className="hidden lg:block" />
              <div className="relative z-10 flex flex-col">
                <div className="font-semibold">Free</div>
                <div className="text-zinc-400">Get started today</div>
                <div className="mt-6">
                  <Skeleton
                    disabled={!!props.prices}
                    className={`font-semibold ${
                      c(monthlyPrice, curr).length > 6 ? "text-xl" : "text-4xl"
                    }`}
                    defaultValue="€0"
                  >
                    {c(0, curr)}
                  </Skeleton>
                </div>
                <div className="mt-4 text-zinc-400 lg:hidden">
                  Get 15 free conversions on us, every month.
                </div>
                <div className="max-w-[180px] pr-4">
                  <div className="mt-6">
                    <Link href="/install">
                      <Button>Get the plugin</Button>
                    </Link>
                  </div>
                  <div className="mt-2.5 h-5" />
                </div>
              </div>
              <div className="relative col-span-2">
                <div className="relative z-10 font-semibold">Professional</div>
                <div className="absolute left-28 right-0 top-3 h-px bg-zinc-700" />
                <div className="grid gap-4 sm:grid-cols-2 sm:gap-12 lg:gap-0">
                  <div className="relative">
                    <div className="relative z-10">
                      <div className="text-zinc-400">Monthly subscription</div>
                      <div
                        className={`mt-6 font-semibold ${
                          c(monthlyPrice, curr).length > 6
                            ? "text-xl"
                            : "text-4xl"
                        }`}
                      >
                        <span className="mr-2 font-light text-zinc-400/60 line-through">
                          <Skeleton
                            disabled={!!props.prices}
                            defaultValue="€16"
                          >
                            {c(monthlyPrice * 1.6, curr)}
                          </Skeleton>
                        </span>
                        <Skeleton disabled={!!props.prices} defaultValue="€16">
                          {c(monthlyPrice, curr)}
                        </Skeleton>
                        <span className="text-base text-zinc-400">/month</span>
                      </div>
                      <div className="mt-4 text-zinc-400 lg:hidden">
                        Upgrade to get all professional features, including
                        unlimited conversions.
                      </div>

                      <div className="mt-6 max-w-[180px] pb-5 pr-4 lg:pb-0">
                        <Button
                          onClick={subscribe}
                          disabled={alreadySubscribed}
                        >
                          {alreadySubscribed ? "✓" : "Subscribe"}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <div className="relative z-10">
                      <div className="pointer-events-none absolute -inset-10 right-10 hidden rounded-full bg-sky-400/20 blur-2xl lg:block" />
                      <div className="flex items-end text-zinc-400">
                        <div>Annual subscription</div>
                      </div>
                      <div
                        className={`mt-6 font-semibold ${
                          c(annualPrice, curr).length > 6
                            ? "text-xl"
                            : "text-4xl"
                        }`}
                      >
                        <span className="mr-2 font-light text-zinc-400/60 line-through">
                          <Skeleton
                            disabled={!!props.prices}
                            defaultValue="€16"
                          >
                            {c((annualPrice / 12) * 1.6, curr)}
                          </Skeleton>
                        </span>
                        <Skeleton disabled={!!props.prices} defaultValue="€199">
                          {c(annualPrice / 12, curr)}
                          <span className="text-base text-zinc-400">
                            /month
                          </span>
                        </Skeleton>
                      </div>
                      <div className="mt-4 text-zinc-400 lg:hidden">
                        Pay annually and get the best value: 3 months a year for
                        free.
                      </div>

                      <div className="mt-6 max-w-[180px] pb-5 pr-4 lg:pb-0">
                        <Button onClick={buy} disabled={alreadySubscribed}>
                          {alreadySubscribed ? "✓" : "Subscribe"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 hidden text-sm text-zinc-300 lg:ml-0 lg:flex">
                  <div>
                    <span className="relative z-10 mr-1.5 rounded-full bg-amber-900 px-2 py-1 text-xs text-amber-300">
                      Best value
                    </span>{" "}
                    Get{" "}
                    <span className="font-medium text-zinc-100">3 months</span>{" "}
                    a year for free.
                  </div>
                  <svg
                    width="15"
                    height="12"
                    viewBox="0 0 15 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="ml-2.5 h-3"
                    style={{ transform: "rotateY(180deg)" }}
                  >
                    <path
                      d="M14 11C5 11.5 5.5 5 4 2"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1.5 5.5L4 1.5L8 3.5"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <Row
              newGroup
              variants={rowVariants1}
              title="Conversions"
              tooltip="Converting a layer to code counts as 1 conversion. Unlimited conversions are on a fair-use basis for one user."
            >
              <div>15 per month</div>
              <div className="flex items-center space-x-2">
                <div>Unlimited</div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-5 w-5 text-amber-400"
                >
                  <path d="M15.98 1.804a1 1 0 00-1.96 0l-.24 1.192a1 1 0 01-.784.785l-1.192.238a1 1 0 000 1.962l1.192.238a1 1 0 01.785.785l.238 1.192a1 1 0 001.962 0l.238-1.192a1 1 0 01.785-.785l1.192-.238a1 1 0 000-1.962l-1.192-.238a1 1 0 01-.785-.785l-.238-1.192zM6.949 5.684a1 1 0 00-1.898 0l-.683 2.051a1 1 0 01-.633.633l-2.051.683a1 1 0 000 1.898l2.051.684a1 1 0 01.633.632l.683 2.051a1 1 0 001.898 0l.683-2.051a1 1 0 01.633-.633l2.051-.683a1 1 0 000-1.898l-2.051-.683a1 1 0 01-.633-.633L6.95 5.684zM13.949 13.684a1 1 0 00-1.898 0l-.184.551a1 1 0 01-.632.633l-.551.183a1 1 0 000 1.898l.551.183a1 1 0 01.633.633l.183.551a1 1 0 001.898 0l.184-.551a1 1 0 01.632-.633l.551-.183a1 1 0 000-1.898l-.551-.184a1 1 0 01-.633-.632l-.183-.551z" />
                </svg>
              </div>
              <div className="flex items-center space-x-2">
                <div>Unlimited</div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-5 w-5 text-amber-400"
                >
                  <path d="M15.98 1.804a1 1 0 00-1.96 0l-.24 1.192a1 1 0 01-.784.785l-1.192.238a1 1 0 000 1.962l1.192.238a1 1 0 01.785.785l.238 1.192a1 1 0 001.962 0l.238-1.192a1 1 0 01.785-.785l1.192-.238a1 1 0 000-1.962l-1.192-.238a1 1 0 01-.785-.785l-.238-1.192zM6.949 5.684a1 1 0 00-1.898 0l-.683 2.051a1 1 0 01-.633.633l-2.051.683a1 1 0 000 1.898l2.051.684a1 1 0 01.633.632l.683 2.051a1 1 0 001.898 0l.683-2.051a1 1 0 01.633-.633l2.051-.683a1 1 0 000-1.898l-2.051-.683a1 1 0 01-.633-.633L6.95 5.684zM13.949 13.684a1 1 0 00-1.898 0l-.184.551a1 1 0 01-.632.633l-.551.183a1 1 0 000 1.898l.551.183a1 1 0 01.633.633l.183.551a1 1 0 001.898 0l.184-.551a1 1 0 01.632-.633l.551-.183a1 1 0 000-1.898l-.551-.184a1 1 0 01-.633-.632l-.183-.551z" />
                </svg>
              </div>
            </Row>
            <Row
              variants={rowVariants2}
              title="Detect types"
              new
              tooltip="Frontender automatically recognizes buttons, input fields, headings and more."
            >
              <Check notSoon />
              <Check notSoon />
              <Check notSoon />
            </Row>
            <Row
              variants={rowVariants2}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
                  />
                </svg>
              }
              title="Instant code"
              tooltip="Select something in Figma to immediately turn it into code, without manually needing to confirm."
            >
              <Cross />
              <Check notSoon />
              <Check notSoon />
            </Row>
            <Row
              variants={rowVariants2}
              title="Autocopy"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 7.5V6.108c0-1.135.845-2.098 1.976-2.192.373-.03.748-.057 1.123-.08M15.75 18H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08M15.75 18.75v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5A3.375 3.375 0 006.375 7.5H5.25m11.9-3.664A2.251 2.251 0 0015 2.25h-1.5a2.251 2.251 0 00-2.15 1.586m5.8 0c.065.21.1.433.1.664v.75h-6V4.5c0-.231.035-.454.1-.664M6.75 7.5H4.875c-.621 0-1.125.504-1.125 1.125v12c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V16.5a9 9 0 00-9-9z"
                  />
                </svg>
              }
              tooltip="Copy the code to your clipboard automatically after every conversion, so you can paste it in your editor immediately."
            >
              <Cross />
              <Check notSoon />
              <Check notSoon />
            </Row>
            <Row
              variants={rowVariants2}
              new
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z"
                  />
                </svg>
              }
              title="React & Vue components"
              tooltip="Create entire components ready to paste in your editor, even with optional TypeScript props."
            >
              <Cross />
              <Check notSoon />
              <Check notSoon />
            </Row>
            <Row
              variants={rowVariants2}
              title="Custom Tailwind config"
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
                  />
                </svg>
              }
              tooltip="Let Frontender use whatever Tailwind config you provide. It can also find the closest match if there is not an exact one."
            >
              <Cross />
              <Check notSoon />
              <Check notSoon />
            </Row>
            <Row
              variants={rowVariants2}
              endGroup
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                  />
                </svg>
              }
              title="Support an indie developer"
              tooltip="By paying for Frontender, you allow me (an indie developer) to spend more time improving the Frontender code engine and add new features. Thank you!"
            >
              <Cross />
              <Check notSoon />
              <Check notSoon />
            </Row>

            {/* <Row
            title="Responsive variants"
            newGroup
            tooltip="Add media queries (CSS) or responsive variants (Tailwind) to your code."
          >
            <Cross />
            <Check />
            <Check />
          </Row>
          <Row
            title="Hover, focus & active variants"
            tooltip="Add hover, focus and active pseudo-classes (CSS) or variants (Tailwind) to your code."
          >
            <Cross />
            <Check />
            <Check />
          </Row>
          <Row
            title="Light & dark mode variants"
            endGroup
            tooltip="Add specific styles to use in light and dark mode using the prefers-color-scheme feature (CSS) or the dark: variant (Tailwind)."
          >
            <Cross />
            <Check />
            <Check />
          </Row>

          <Row
            title="Team support"
            newGroup
            tooltip="Invite multiple people and have one centralized place for all controls and billing."
          >
            <Cross />
            <Cross />
            <Check />
          </Row>
          <Row
            title="Design system rules"
            endGroup
            tooltip="Specify specific conversion rules so that Frontender matches your design system logic for all developers in your organization."
          >
            <Cross />
            <Cross />
            <Check />
          </Row> */}
          </motion.div>
          <Footer
            acceptedCookies={props.acceptedCookies}
            setAcceptedCookies={props.setAcceptedCookies}
          />
        </div>
        <div className="animate-blob pointer-events-none fixed -right-[60vw] -top-[30vh] h-[120vh] w-[120vw] rounded-full bg-sky-700/80 opacity-20 blur-3xl" />
      </motion.div>
    </>
  )
}

PricingPage.displayName = "PricingPage"
